import { Box, FormLabel, TextField } from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AutoCompleteSelect from "../../../../../components/autocomplete/autoCompleteSelect/AutoCompleteSelect";
import useBusinessPartnersQuery from "../../../../../hooks/graphql-hooks/queries/useBusinessPartnersQuery";
import useCarrierConfigurationSelectOptionsQuery from "../../../../../hooks/graphql-hooks/queries/useCarrierConfigurationSelectOptionsQuery";
import { LocationFragment } from "../../../../../models/autogenerated-deliveryService-gql/graphql";
import { IOutboundGeneralInfo } from "../../../../../models/outboundGeneralInfo";
import { isValidString } from "../../../../../utils/helper";
import "./OutboundGeneralInfoForm.scss";

interface CarrierOption {
  id: string;
  label: string;
  value: string;
}

interface BpOption {
  id: string;
  number: string;
  companyName: string;
}

interface OutboundGeneralInfoFormProps {
  formData: IOutboundGeneralInfo;
  onChange: (fieldID: string, text: string) => void;
  locations: LocationFragment[];
}

const OutboundGeneralInfoForm = (props: OutboundGeneralInfoFormProps) => {
  const { t } = useTranslation();

  const { formData, onChange, locations } = props;

  const [carrierOptions, setCarrierOptions] = useState<CarrierOption[]>([]);
  const [allBusinessPartners, setAllBusinessPartners] = useState<BpOption[]>(
    [],
  );
  const [recipientCompanyOptions, setRecipientCompanyOptions] = useState<
    string[]
  >([]);
  const [senderCompanyOptions, setSenderCompanyOptions] = useState<string[]>(
    [],
  );

  const [queryBusinessPartners] = useBusinessPartnersQuery({
    dataAction: (collection) => {
      setAllBusinessPartners(
        collection.data.map((bp) => ({
          id: String(bp.id),
          number: bp.number ?? "",
          companyName: bp.companyName ?? "",
        })),
      );
    },
  });

  const [carrierQuery] = useCarrierConfigurationSelectOptionsQuery({
    dataAction: (collection) => {
      const options = collection.data.map((carrier: CarrierOption) => ({
        id: carrier.id,
        label: carrier.label,
        value: carrier.value,
      }));
      setCarrierOptions(options);
    },
  });

  useEffect(() => {
    carrierQuery({
      variables: { searchString: "", page: 1, pageSize: 100 },
    }).catch(() => {});
    queryBusinessPartners({
      variables: { searchText: "", offset: 0, limit: 100 },
    }).catch(() => {});
  }, []);

  const locationOptions = locations?.map((l) => l.name).filter(isValidString);

  /* Helper Functions for Autocomplete fields */
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;

    onChange(id, value);
  };

  const renderTextField = (
    label: keyof IOutboundGeneralInfo,
    required?: boolean,
  ) => (
    <>
      <FormLabel required={required}>{t(label)}</FormLabel>
      <TextField
        data-testid={`outboundGeneralInfo-form-textfield-${label}`}
        id={label}
        className="input-element"
        value={formData[label] || ""}
        onChange={handleChange}
        variant="outlined"
        placeholder={t(label + "Placeholder")}
        required={required}
      />
    </>
  );

  const renderAutoCompleteSelect = (
    label: keyof IOutboundGeneralInfo,
    options: string[],
    required?: boolean,
  ) => (
    <AutoCompleteSelect
      fieldId={label}
      options={options}
      onSelect={onChange}
      initialValue={formData[label] || ""}
      required={required}
    />
  );

  useEffect(() => {
    const filteredRecipients = allBusinessPartners
      .filter((bp) => bp.number !== formData.sender)
      .map((bp) => bp.companyName);

    const filteredSenders = allBusinessPartners
      .filter((bp) => bp.number !== formData.recipient)
      .map((bp) => bp.companyName);

    setRecipientCompanyOptions(filteredRecipients);
    setSenderCompanyOptions(filteredSenders);
  }, [allBusinessPartners, formData.sender, formData.recipient]);

  return (
    <Box
      className="form-flex-container"
      component="form"
      sx={{ width: "100%" }}
      autoComplete="off"
    >
      {renderTextField("deliveryNumber", true)}
      {renderAutoCompleteSelect("location", locationOptions || [], true)}
      {renderAutoCompleteSelect("recipient", recipientCompanyOptions, true)}
      {renderAutoCompleteSelect("sender", senderCompanyOptions, false)}
      {renderAutoCompleteSelect(
        "carrier",
        carrierOptions.map((carrier) => carrier.label) || [],
        false,
      )}
    </Box>
  );
};
export default OutboundGeneralInfoForm;
